import * as XLSX from "xlsx";

export const PRODUCT_IMPORT_MAX_ROWS = 1200;

export const PRODUCT_IMPORT_HEADERS = [
  "FECHA_REGISTRO",
  "SKU",
  "CATEGORIA",
  "MARCA",
  "MODELO",
  "RAM",
  "ALMACENAMIENTO",
  "COLOR",
  "CODIGO_MODELO",
  "IMEI_1",
  "IMEI_2",
  "RUC_EMPRESA",
  "PROVEEDOR",
  "N_FACTURA",
  "PRECIO_COMPRA",
  "PRECIO_VENTA",
  "ALMACEN",
  "AFECTACION_IGV",
  "DESCRIPCION",
  "DESTINO_SEDE",
];

const normalizeCell = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeHeader = (value) =>
  normalizeCell(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const catalogRows = (meta = {}) => {
  const rows = [["TIPO", "VALOR", "DETALLE"]];
  (meta.categories || []).forEach((item) => rows.push(["CATEGORIA", item.name || "", item.slug || ""]));
  (meta.brands || []).forEach((item) => rows.push(["MARCA", item.name || "", ""]));
  (meta.models || []).forEach((item) => {
    const brand = (meta.brands || []).find((brandItem) => Number(brandItem.id) === Number(item.brand_id));
    rows.push(["MODELO", item.name || "", brand?.name || ""]);
  });
  (meta.rucs || []).forEach((item) => rows.push(["RUC_EMPRESA", item.business_name || item.alias || "", item.ruc || ""]));
  (meta.suppliers || []).forEach((item) => rows.push(["PROVEEDOR", item.business_name || item.name || "", item.ruc || ""]));
  const physicalLocationKeys = new Set();
  (meta.locations || [])
    .filter((item) =>
      ["CENTRAL_WAREHOUSE", "BRANCH"].includes(String(item.type || "").toUpperCase()) &&
      Number(item.is_active ?? 1) !== 0,
    )
    .forEach((item) => {
      const name = item.name || item.sedeName || "";
      if (!name) return;
      physicalLocationKeys.add(normalizeHeader(name));
      rows.push([
        "ALMACEN",
        name,
        String(item.type || "").toUpperCase() === "BRANCH" ? "SEDE / TIENDA" : item.city || "ALMACEN CENTRAL",
      ]);
    });

  // Una sede activa puede ser nueva y todavía no tener su ubicación BRANCH creada.
  // Se incluye igualmente en la plantilla: el backend la materializa de forma segura
  // al importar, sin tratarla como Almacén Central.
  (meta.sedes || []).forEach((item) => {
    const name = item.name || "";
    if (!name || physicalLocationKeys.has(normalizeHeader(name))) return;
    rows.push(["ALMACEN", name, "SEDE / TIENDA"]);
  });
  return rows;
};

export function downloadProductImportTemplate(meta = {}) {
  const workbook = XLSX.utils.book_new();
  const category = (meta.categories || []).find((item) => String(item.slug || "").toLowerCase() === "celulares") || (meta.categories || [])[0] || {};
  const brand = (meta.brands || [])[0] || {};
  const model = (meta.models || []).find((item) => Number(item.brand_id) === Number(brand.id) && (!category.id || Number(item.category_id) === Number(category.id))) || (meta.models || [])[0] || {};
  const ruc = (meta.rucs || [])[0] || {};
  const supplier = (meta.suppliers || [])[0] || {};
  const warehouse =
    (meta.locations || []).find(
      (item) =>
        ["CENTRAL_WAREHOUSE", "BRANCH"].includes(String(item.type || "").toUpperCase()) &&
        Number(item.is_active ?? 1) !== 0,
    ) || (meta.sedes || [])[0] || {};
  const example = {
    FECHA_REGISTRO: "",
    SKU: "",
    CATEGORIA: category.name || "CELULARES",
    MARCA: brand.name || "SAMSUNG",
    MODELO: model.name || "GALAXY A06",
    RAM: "4GB",
    ALMACENAMIENTO: "64GB",
    COLOR: "GREEN",
    CODIGO_MODELO: "",
    IMEI_1: "625353627368555",
    IMEI_2: "",
    RUC_EMPRESA: ruc.business_name || ruc.alias || ruc.ruc || "EMPRESA",
    PROVEEDOR: supplier.business_name || supplier.name || supplier.ruc || "PROVEEDOR",
    N_FACTURA: "FOT-123",
    PRECIO_COMPRA: "299.00",
    PRECIO_VENTA: "599.00",
    ALMACEN: warehouse.name || "ALMACÉN CENTRAL",
    AFECTACION_IGV: "20",
    DESCRIPCION: "",
    DESTINO_SEDE: "",
  };

  const productSheet = XLSX.utils.aoa_to_sheet([PRODUCT_IMPORT_HEADERS]);
  const exampleSheet = XLSX.utils.json_to_sheet([example], { header: PRODUCT_IMPORT_HEADERS });
  productSheet["!cols"] = PRODUCT_IMPORT_HEADERS.map((header) => ({
    wch: ["DESCRIPCION", "RUC_EMPRESA", "PROVEEDOR", "ALMACEN", "DESTINO_SEDE"].includes(header) ? 28 : 18,
  }));
  exampleSheet["!cols"] = PRODUCT_IMPORT_HEADERS.map((header) => ({
    wch: ["DESCRIPCION", "RUC_EMPRESA", "PROVEEDOR", "ALMACEN", "DESTINO_SEDE"].includes(header) ? 28 : 18,
  }));
  ["B", "J", "K"].forEach((column) => {
    for (let row = 2; row <= PRODUCT_IMPORT_MAX_ROWS + 2; row += 1) {
      const ref = `${column}${row}`;
      if (!productSheet[ref]) productSheet[ref] = { t: "s", v: "" };
      productSheet[ref].z = "@";
    }
  });

  const instructions = [
    ["PLANTILLA PROFESIONAL · IMPORTACIÓN DE CELULARES"],
    ["Regla", "Detalle"],
    ["Una fila = un equipo físico", "Cada celular debe registrarse en una fila independiente."],
    ["Fecha de registro", "FECHA_REGISTRO es opcional. Si tiene fecha, esa fecha se conserva como fecha histórica de ingreso. Si queda vacía, el sistema usa la fecha/hora actual."],
    ["IMEI 1", "Obligatorio. Exactamente 15 dígitos. No puede repetirse en el Excel ni en la base de datos."],
    ["IMEI 2", "Opcional. Si se registra, debe tener 15 dígitos y tampoco puede repetirse."],
    ["SKU", "Opcional. Si se deja vacío, el sistema genera un SKU único de 10 dígitos."],
    ["Título", "No se escribe. El sistema lo genera con Marca + Modelo + RAM + Almacenamiento + Código de modelo + Color."],
    ["Catálogos", "Categoría, marca, modelo, RUC y proveedor deben existir. ALMACEN acepta un Almacén Central o una Sede/Tienda activa."],
    ["Stock", "No se edita. Cada fila genera stock 1 en la ubicación física indicada. Si ALMACEN es una sede activa aún sin ubicación BRANCH, el backend la crea de forma transaccional."],
    ["Destino de traslado", "DESTINO_SEDE es opcional. Si se informa y es distinta al almacén inicial, al finalizar la importación se crea un traslado DRAFT agrupado por origen y destino."],
    ["Importación transaccional", "Si existe una sola fila con error, no se registra ningún producto."],
    ["Máximo por archivo", `${PRODUCT_IMPORT_MAX_ROWS} equipos.`],
    ["Afectación IGV", "Valores admitidos: 10, 20, 30 o 40. Si se deja vacío se usa 20."],
  ];
  const instructionSheet = XLSX.utils.aoa_to_sheet(instructions);
  instructionSheet["!cols"] = [{ wch: 28 }, { wch: 88 }];

  const catalogsSheet = XLSX.utils.aoa_to_sheet(catalogRows(meta));
  catalogsSheet["!cols"] = [{ wch: 22 }, { wch: 42 }, { wch: 30 }];

  XLSX.utils.book_append_sheet(workbook, productSheet, "PRODUCTOS");
  XLSX.utils.book_append_sheet(workbook, instructionSheet, "INSTRUCCIONES");
  XLSX.utils.book_append_sheet(workbook, catalogsSheet, "CATALOGOS");
  XLSX.utils.book_append_sheet(workbook, exampleSheet, "EJEMPLO");

  const array = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  downloadBlob(
    new Blob([array], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `plantilla_importacion_celulares_${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
}

export async function parseProductImportFile(file) {
  if (!file) throw new Error("Selecciona un archivo Excel.");
  const extension = String(file.name || "").toLowerCase();
  if (!extension.endsWith(".xlsx") && !extension.endsWith(".xls")) {
    throw new Error("El archivo debe ser Excel (.xlsx o .xls).");
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellDates: false });
  const sheetName = workbook.SheetNames.find((name) => normalizeHeader(name) === "PRODUCTOS") || workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error("No se encontró la hoja PRODUCTOS.");

  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false, blankrows: false });
  if (!matrix.length) throw new Error("La hoja PRODUCTOS está vacía.");

  const headers = matrix[0].map(normalizeHeader);
  const requiredHeaders = [
    "MARCA",
    "MODELO",
    "RAM",
    "ALMACENAMIENTO",
    "COLOR",
    "IMEI_1",
    "RUC_EMPRESA",
    "PROVEEDOR",
    "PRECIO_COMPRA",
    "PRECIO_VENTA",
    "ALMACEN",
  ];
  const missing = requiredHeaders.filter((header) => !headers.includes(header));
  if (missing.length) throw new Error(`Faltan columnas obligatorias en la plantilla: ${missing.join(", ")}.`);

  const rows = matrix.slice(1).map((values, index) => {
    const row = { __rowNumber: index + 2 };
    headers.forEach((header, columnIndex) => {
      if (header) row[header] = normalizeCell(values[columnIndex]);
    });
    return row;
  }).filter((row) =>
    PRODUCT_IMPORT_HEADERS.some((header) => normalizeCell(row[header]) !== ""),
  );

  if (!rows.length) throw new Error("No hay productos para importar en la hoja PRODUCTOS.");
  if (rows.length > PRODUCT_IMPORT_MAX_ROWS) throw new Error(`El archivo supera el máximo de ${PRODUCT_IMPORT_MAX_ROWS} equipos por importación.`);
  return rows;
}
